/**
 * Live-journey-ping telemetry → the EXISTING call-telemetry pipeline
 * (`recordCallFlow` → `recordCallTelemetry` AppSync mutation → CloudWatch
 * `CALLTELEM` lines + the call-telemetry table). No new backend resource.
 *
 * Two sources, one stream, keyed by the session uuid in the `callId` slot so
 * `node infrastructure/scripts/debug-environment.mjs --telemetry <sessionUuid>`
 * returns the whole picture:
 *
 *  - `liveping:js:*`                — the app-wide controller (enabled/disabled,
 *    why a ping was not possible);
 *  - `native:<platform>:liveping:*` — events queued by the native pinger while
 *    JS was suspended (arm / disarm(reason) / refuse(reason) / post(status) /
 *    backoff / foreground flips / FGS start-stop / decision summaries), pulled
 *    via `BackgroundLocation.drainTelemetry()` when JS next runs.
 *
 * PRIVACY: codes, reasons, counts and durations only. Native never emits
 * coordinates or tokens; this forwarder additionally drops any field whose
 * name looks like one and any non-primitive value, so a future native change
 * cannot leak them through here.
 */

import { getNativePlatform } from '../utils/platform'

const MAX_FORWARD_PER_DRAIN = 100
/** Field names that must never reach telemetry, whatever native sends. */
const FORBIDDEN_FIELD = /token|auth|secret|password|^lat(itude)?$|^lng$|^lon(gitude)?$|coord|position|address/i

type Primitive = string | number | boolean

/**
 * Lazy: the call-flow log drags in Amplify, which the pure location service
 * (and its tests) must not load. Telemetry is fire-and-forget — a load
 * failure is swallowed.
 */
function record(event: string, data: Record<string, unknown> | undefined, callId: string | null): void {
  void import('../debug/call-flow-log')
    .then(({ recordCallFlow }) => recordCallFlow(event, data, { callId }))
    .catch(() => {})
}

function isPrimitive(value: unknown): value is Primitive {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
}

function sanitize(fields: Record<string, unknown>): Record<string, Primitive> {
  const out: Record<string, Primitive> = {}
  for (const [key, value] of Object.entries(fields)) {
    if (FORBIDDEN_FIELD.test(key) || !isPrimitive(value)) continue
    out[key] = typeof value === 'string' ? value.slice(0, 120) : value
  }
  return out
}

/** A JS-side controller event (`liveping:js:<name>`). */
export function recordLivePingEvent(
  name: string,
  data: Record<string, unknown> | undefined,
  sessionId: string | null,
): void {
  record(`liveping:js:${name}`, data ? sanitize(data) : undefined, sessionId)
}

/**
 * Forward events drained from the native pinger (`{ event: 'liveping:arm', ts, ... }`).
 * Returns how many were forwarded.
 */
export function forwardNativeLivePingTelemetry(events: unknown, sessionId: string | null): number {
  if (!Array.isArray(events) || events.length === 0) return 0
  const platform = getNativePlatform()
  let forwarded = 0
  for (const raw of events.slice(-MAX_FORWARD_PER_DRAIN)) {
    if (!raw || typeof raw !== 'object') continue
    const { event, ts, ...rest } = raw as Record<string, unknown>
    if (typeof event !== 'string' || !event.startsWith('liveping:')) continue
    record(
      `native:${platform}:${event.slice(0, 60)}`,
      { ...(typeof ts === 'number' ? { nativeTs: ts } : {}), ...sanitize(rest) },
      sessionId,
    )
    forwarded += 1
  }
  return forwarded
}
